import { Controller } from "@hotwired/stimulus"

// Google Places autocomplete on an address field, filling the city (and postal
// code) from the chosen place.
//
// The city target can be either:
//   * a free-text input — the locality is written into it, or
//   * a <select> of the cities we actually cover — the locality is matched
//     against the options. A place outside those cities is reported instead of
//     silently ignored, which is the whole point on the booking forms: the
//     photographer is dispatched by city, so the address and the city have to
//     agree.
//
// Optional values:
//   country  — ISO-3166 alpha-2, restricts suggestions to that country
//
// Optional targets:
//   feedback — element that receives the match/mismatch message
export default class extends Controller {
  static targets = ["address", "city", "postalCode", "feedback"]
  static values = { country: String }

  connect() {
    if (!this.hasAddressTarget) return // nothing to attach to

    if (this.placesReady()) {
      this.initializeAutocomplete()
    } else {
      this.boundInit = this.initializeAutocomplete.bind(this)
      document.addEventListener("google-maps-callback", this.boundInit)
      // If Maps never loads (API disabled, quota, blocked referrer) the field
      // would sit there looking broken. Say so instead — the address is still
      // typeable by hand.
      this.loadTimer = setTimeout(() => {
        if (!this.autocomplete) {
          this.setFeedback(
            "La búsqueda de direcciones no está disponible en este momento — escriba la dirección y elija la ciudad manualmente.",
            "warn"
          )
        }
      }, 8000)
    }
  }

  disconnect() {
    if (this.boundInit) document.removeEventListener("google-maps-callback", this.boundInit)
    if (this.loadTimer) clearTimeout(this.loadTimer)
  }

  placesReady() {
    return typeof google === "object" && !!google.maps?.places?.Autocomplete
  }

  initializeAutocomplete() {
    if (this.autocomplete) return
    if (this.loadTimer) clearTimeout(this.loadTimer)
    if (!this.placesReady()) return

    const options = { fields: ["address_components", "geometry", "name", "formatted_address"] }
    if (this.countryValue) {
      options.componentRestrictions = { country: this.countryValue.toLowerCase() }
    }

    this.autocomplete = new google.maps.places.Autocomplete(this.addressTarget, options)
    this.autocomplete.addListener("place_changed", this.placeChanged.bind(this))

    // Picking a suggestion with Enter would otherwise submit the booking form.
    this.addressTarget.addEventListener("keydown", (event) => {
      if (event.key === "Enter") event.preventDefault()
    })
  }

  placeChanged() {
    const place = this.autocomplete.getPlace()
    if (!place || !place.geometry) {
      this.setFeedback("No pudimos leer esa dirección. Elija una opción de la lista.", "error")
      return
    }

    const parts = this.componentsOf(place)

    if (place.formatted_address) this.addressTarget.value = place.formatted_address
    if (this.hasPostalCodeTarget && parts.postalCode) this.postalCodeTarget.value = parts.postalCode
    this.applyCity(parts)
  }

  // Google's "city" is not one field: depending on the country it lands in
  // locality, or in one of the administrative_area levels (common in BR/MX),
  // or in a sublocality for large metros. Try them in decreasing precision.
  componentsOf(place) {
    const pick = (type) =>
      place.address_components?.find((c) => c.types.includes(type))?.long_name || ""

    return {
      candidates: [
        pick("locality"),
        pick("postal_town"),
        pick("administrative_area_level_2"),
        pick("sublocality_level_1"),
        pick("administrative_area_level_1")
      ].filter((v) => v.length > 0),
      postalCode: pick("postal_code")
    }
  }

  applyCity({ candidates }) {
    if (!this.hasCityTarget) return

    if (candidates.length === 0) {
      this.setFeedback("No pudimos determinar la ciudad de esa dirección — elíjala manualmente.", "warn")
      return
    }

    // Free-text city field: take the most precise candidate.
    if (this.cityTarget.tagName !== "SELECT") {
      this.cityTarget.value = candidates[0]
      this.setFeedback("")
      return
    }

    const option = this.matchOption(candidates)
    if (option) {
      this.cityTarget.value = option.value
      this.cityTarget.dispatchEvent(new Event("change", { bubbles: true }))
      this.setFeedback(`Ciudad detectada: ${option.text.trim()}.`, "ok")
    } else {
      this.setFeedback(
        `${candidates[0]} no está entre las ciudades disponibles. Elija la ciudad más cercana o verifique la dirección.`,
        "warn"
      )
    }
  }

  matchOption(candidates) {
    const options = Array.from(this.cityTarget.options).filter((o) => o.value !== "")

    for (const candidate of candidates) {
      const needle = this.normalize(candidate)
      if (!needle) continue

      const exact = options.find((o) => this.normalize(o.value) === needle)
      if (exact) return exact

      // "Ciudad de México" vs "México", "São Paulo" vs "Sao Paulo - SP", …
      const partial = options.find((o) => {
        const hay = this.normalize(o.value)
        return hay.includes(needle) || needle.includes(hay)
      })
      if (partial) return partial
    }
    return null
  }

  // Case/accent/punctuation-insensitive: "Bogotá D.C." and "bogota dc" match.
  normalize(value) {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")
  }

  setFeedback(message, tone = "neutral") {
    if (!this.hasFeedbackTarget) return
    this.feedbackTarget.textContent = message
    this.feedbackTarget.hidden = message.length === 0
    this.feedbackTarget.style.color =
      tone === "ok"   ? "var(--piktiz-green)" :
      tone === "warn" ? "#c98a00"             :
      tone === "error"? "var(--piktiz-pink)"  : ""
  }
}
